import { MouseEvent, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@apollo/client'
import { useTranslation } from 'react-i18next'
import { Box, debounce, IconButton } from '@mui/material'

import Select from 'components/global/CUK/AsyncSelect/Select'
import { OptionSingle } from 'components/global/CUK/AsyncSelect/components/Option'

import { OPERATION_SEARCH_ACCOUNTS_UNSCOPED } from '../operations-gql/queries'
import { AccountUnscope } from '../types'

const SEARCH_DEBOUNCE_DELAY = 300

type AccountsUnscopedData = {
  accounts: AccountUnscope[]
}

type AccountOption = AccountUnscope & {
  selectedLabel: string
}

type SelectChangeValue = AccountOption | Record<string, never> | null

type AccountSelectProps = {
  value: AccountUnscope | null
  onChange: (account: AccountUnscope | null) => void
}

const isAccountOption = (option: SelectChangeValue): option is AccountOption =>
  Boolean(option && 'sf_id' in option)

const OptionHighlightedName = props => {
  const { data, selectProps } = props
  const shouldHighlight = Boolean(selectProps.inputValue && data.highlight_name)

  return (
    <OptionSingle
      {...props}
      label={
        shouldHighlight
          ? <Box component="span" dangerouslySetInnerHTML={{ __html: data.highlight_name }} />
          : data.name
      }
    />
  )
}

const ClearIndicator = ({ selectProps }) => {
  const { t } = useTranslation()

  const handleMouseDown = (event: MouseEvent) => {
    event.stopPropagation()
  }

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation()
    selectProps.onChange(null)
  }

  return (
    <IconButton
      size="small"
      aria-label={t('ui_elements.reset')}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      sx={{ p: 0.5, fontSize: 12, color: 'content.secondary' }}
    >
      <i className="fa-regular fa-xmark" aria-hidden="true" />
    </IconButton>
  )
}

const AccountSelect = ({ value, onChange }: AccountSelectProps) => {
  const { t } = useTranslation()
  const [search, setSearch] = useState('')
  const placeholder = t('admin_sites.filters.account')

  const debouncedSearchChange = useMemo(
    () => debounce((nextSearch: string) => setSearch(nextSearch.trim()), SEARCH_DEBOUNCE_DELAY),
    []
  )

  useEffect(() => () => debouncedSearchChange.clear(), [debouncedSearchChange])

  const { data, loading } = useQuery<AccountsUnscopedData>(OPERATION_SEARCH_ACCOUNTS_UNSCOPED, {
    variables: { name: search },
    skip: !search
  })

  const toOption = (account: AccountUnscope): AccountOption => ({
    ...account,
    selectedLabel: t('ui_elements.count_selected', { count: 1 })
  })

  const selectedOption = value ? toOption(value) : null

  const getOptions = () => {
    if (search) return data ? data.accounts.map(toOption) : []
    return selectedOption ? [selectedOption] : []
  }

  const getNoOptionsLabel = () => {
    if (!search) return t('admin_sites.filters.account_search_hint')
    if (loading) return t('ui_elements.loading')
    return t('admin_sites.filters.no_accounts')
  }

  const handleInputChange = (inputValue: string) => {
    debouncedSearchChange(inputValue)
  }

  const handleChange = (option: SelectChangeValue) => {
    if (!isAccountOption(option)) {
      onChange(null)
      return
    }
    const { id, sf_id, name, highlight_name } = option
    onChange({ id, sf_id, name, highlight_name })
  }

  return (
    <Select
      /* @ts-expect-error JSX */
      isSearchable
      isClearable
      showSelectedValue={false}
      filterOption={null}
      options={getOptions()}
      mainField="selectedLabel"
      keyField="sf_id"
      placeholder={placeholder}
      value={selectedOption}
      onChange={handleChange}
      onInputChange={handleInputChange}
      noOptionsMessage={getNoOptionsLabel()}
      aria-label={placeholder}
      components={{ Option: OptionHighlightedName, ClearIndicator }}
    />
  )
}

export default AccountSelect
